// The client's own sound triggers: the map's positional voices, and nothing
// else.
//
// The notification cues used to live here too, as a hand-written table of
// "this notice sounds like that". They do not any more. The server ships its
// rule tables (CLAUDE.md), and the event table is one of them: every toast
// and every player-direct cue now comes from `events.json` through the hello
// catalog, and `events.ts` plays what the row names. An operator changes a
// cue in the configuration, not in this file.
//
// What is left is the part the server cannot decide, because it is a picture
// and not a message: a torpedo goes off at a place on the map, and the sound
// belongs to that place. The ids are the trigger vocabulary of
// docs/design/audio.md §4.3-§4.5; a theme directory names its files after
// them, so the map here and the asset bank agree by name and nothing else.
// An id with no variants in the loaded theme plays nothing.
//
// One event needs more than its kind to name a voice: an EXPLOSION is a
// sinking hull, and a sinking BEACON is its own sound. `mapCue` below is the
// one door for that. It takes the event and a way to ask what class the
// victim was, so the table stays a table (R547). The same door keeps a
// combat cue to the two people in the exchange (R551). A round kill, a
// signature order, a flip and a capture fork on the viewer's faction
// (R3118-R3120), so the door takes the faction as well.

import { EventKind, RoundKillSource, ShipClass } from '../gen/proto/aquaphobia';
import type { KnownSfxId } from '../gen/balance';
import {
  AUDIO_FIGHT_QUIET_SEC, AUDIO_FLIP_CUE_GAP_SEC, AUDIO_REPAIR_CUE, AUDIO_REPAIR_SCALE,
} from '../gen/presentation';
import type { SpawnKind } from '../render/spawn-plan';

/**
 * A trigger id. It is also the file-name stem in a theme.
 *
 * It is `string` and not the registered vocabulary, because the menu
 * shell asks for its own `sfx-menu-*` cues (R220-R229) and an event row
 * may name any id an operator's theme holds. The map voices below are
 * narrower: they are the ids audio.md §4.3-§4.5 registers, so they carry
 * the generated `KnownSfxId` and a typo fails the build (R338).
 */
export type SfxId = string;

/** One event's cue. `scale` multiplies the gain at the spawn point.
 *
 * `voice` off plays the EFFECT of the id and never the voice line of the
 * same id. It is for a cue that borrows another cue's effect: the words of
 * the id it borrowed would state something that did not happen (R595).
 * Absent means "speak", so every ordinary row is unchanged. */
export interface EventSfx {
  id: KnownSfxId;
  scale?: number;
  voice?: boolean;
}

/**
 * The Events channel (`EventKind`), which carries a world position — every
 * one of these is positional. An event the map does not show the player
 * never arrives, so detection stays server-enforced here too.
 *
 * TORPEDO_EXPIRE shares the hit clip at half gain: an end-of-flight
 * detonation (R82) is the same charge going off with nothing under it, and
 * it must not read as loudly as a hit. It takes the effect alone: the voice
 * line of that id says "Direct hit.", and a round that hit nothing must not
 * report one (R595, audio.md §4.3).
 */
export const sfxForEventKind: Partial<Record<EventKind, EventSfx>> = {
  [EventKind.EVENT_KIND_LAUNCH]: { id: 'sfx-torpedo-fire' },
  [EventKind.EVENT_KIND_TORPEDO_HIT]: { id: 'sfx-torpedo-explode' },
  [EventKind.EVENT_KIND_TORPEDO_EXPIRE]: { id: 'sfx-torpedo-explode', scale: 0.5, voice: false },
  // The line of this id is "Fleet lost.", which is the victim's report.
  // `mapCue` forks a hull this commander sank to the effect alone, and it
  // forks a sinking Beacon to `sfx-beacon-destroyed` (R730).
  [EventKind.EVENT_KIND_EXPLOSION]: { id: 'sfx-fleet-sink' },
  // The FIRST gun hit this commander lands in a fight. `mapCue` forks the
  // first hit an own hull TAKES to `SFX_TAKING_DAMAGE`, and every later hit
  // of the fight to `SFX_FLEET_HIT` (R629).
  [EventKind.EVENT_KIND_HIT]: { id: 'sfx-fleet-fight' },
  // A FLIP, a CAPTURE, a ROUND_KILL and a SIGNATURE fork on the viewer's
  // faction, so `mapCue` names their cues and this table holds none.
};

/** A cell flips to the own faction: the tier-1 voice, one cue in
 *  `flip_cue_gap_sec` (R3120). A flip of another faction is silent. */
export const SFX_SOV1: EventSfx = { id: 'sfx-sov1-taken' };

/** A structure changes hands (R3080, R3120). The new owner hears
 *  "Strategic object secured.", the old owner "Strategic object lost.", and
 *  a bystander the effect of the capture alone. */
export const SFX_SOV2: EventSfx = { id: 'sfx-sov2-taken' };
const SFX_SOV2_QUIET: EventSfx = { id: 'sfx-sov2-taken', voice: false };
export const SFX_OBJECT_LOST: EventSfx = { id: 'sfx-object-lost' };

/** A guard takes a round out of the water (R3118): the line for the side of
 *  the guard, the effect alone for the side that lost the round. */
export const SFX_GUARD_KILL: EventSfx = { id: 'sfx-guard-kill' };
const SFX_GUARD_KILL_QUIET: EventSfx = { id: 'sfx-guard-kill', voice: false };

/** One round burst, however many rounds it killed (R3118): the line for the
 *  commander who fired the bursting round, the effect alone for a faction
 *  that lost a round. */
export const SFX_TORPOR_BURST: EventSfx = { id: 'sfx-torpor-burst' };
const SFX_TORPOR_BURST_QUIET: EventSfx = { id: 'sfx-torpor-burst', voice: false };

/** An ambush hit this commander's guns land, in place of the fight's
 *  opening line (R3118). A later ambush hit of the fight plays the effect
 *  alone. */
export const SFX_AMBUSH_SHOT: EventSfx = { id: 'sfx-ambush-shot' };
const SFX_AMBUSH_SHOT_QUIET: EventSfx = { id: 'sfx-ambush-shot', voice: false };

/** A signature order is spent (R3119). The line says "Enemy signature
 *  order.", so a hostile spend speaks it and an own spend plays the effect
 *  alone. */
export const SFX_SIGNATURE_ORDER: EventSfx = { id: 'sfx-signature-order' };
const SFX_SIGNATURE_ORDER_QUIET: EventSfx = { id: 'sfx-signature-order', voice: false };

/** The first gun hit an own hull takes in a fight: "We are taking damage."
 *  (audio.md §4.4). It was the cue watcher's client-local edge before R629;
 *  it is positional now, at the struck hull. */
export const SFX_TAKING_DAMAGE: EventSfx = { id: 'sfx-taking-damage' };

/** Every gun hit of a fight after the first, dealt or taken: the effect
 *  alone. A fight is a hit a second, and a line that speaks on every one is
 *  the fight's noise floor and not a report (R629). The id has a line in
 *  the bank ("Hit.", audio.md §4.4) so the bank is complete; `voice: false`
 *  keeps it silent. */
export const SFX_FLEET_HIT: EventSfx = { id: 'sfx-fleet-hit', voice: false };

/** A repair hit: a TORPEDO_HIT with `heal`, which a Wrasse raises on the
 *  own hull it mends (R2783). The torpedo explosion would read as a strike
 *  on that hull, so the hit plays the repair round's own cue (R2804). The
 *  commander who fired the Wrasse hears its line; the commander of the
 *  repaired hull hears the effect alone (R2811). The id and the gain scale
 *  are `[presentation.audio] repair_*` (audio.md §8.5). */
export const SFX_REPAIR: EventSfx = { id: AUDIO_REPAIR_CUE, scale: AUDIO_REPAIR_SCALE };
const SFX_REPAIR_QUIET: EventSfx = { ...SFX_REPAIR, voice: false };

/** A sinking Beacon speaks its own line, and a sink the viewer did not
 *  make plays the effect alone. Each cue is one constant, so an event
 *  makes no object. */
const SFX_BEACON_DESTROYED: EventSfx = { id: 'sfx-beacon-destroyed' };
const SFX_SINK_QUIET: EventSfx = { id: 'sfx-fleet-sink', voice: false };

/** The two sides of a fight this commander is in. */
export type FightSide = 'dealt' | 'taken';

/**
 * The shortest wall gap between two cues of one kind (R3120). The flip cue
 * reads it: a front gains several cells a second, so one cue in
 * `flip_cue_gap_sec` speaks and the other flips of the window stay a
 * picture. `now` is injected so a test drives the clock.
 */
export class CueGap {
  private last = -Infinity;

  constructor(
    private readonly gapSec: number = AUDIO_FLIP_CUE_GAP_SEC,
    private readonly now: () => number = () => performance.now() / 1000,
  ) {}

  /** Answers whether a cue may sound now, and marks it when it may. */
  pass(): boolean {
    const t = this.now();
    if (t - this.last < this.gapSec) return false;
    this.last = t;
    return true;
  }
}

/**
 * Which gun hit opens a fight (R629).
 *
 * A fight has two sides, and each side speaks once: the first hit this
 * commander's guns land says "Engaging the enemy.", the first hit an own
 * hull takes says "We are taking damage.", and every later hit of either
 * side plays the effect alone. A side's fight ends when `fight_quiet_sec`
 * pass with no hit on it, so the next hit there is a first hit again. The
 * clock is per commander and not per hull: one report opens the fight, and
 * a second hull that joins it is the same fight.
 *
 * `now` is injected so a test drives the clock; the scene uses the wall
 * clock, because the quiet is the listener's and not the world's.
 */
export class FightClock {
  private readonly last: Record<FightSide, number> = { dealt: -Infinity, taken: -Infinity };

  constructor(private readonly now: () => number = () => performance.now() / 1000) {}

  /** Marks one hit on `side`, and answers whether it opened the fight there. */
  opens(side: FightSide): boolean {
    const t = this.now();
    const first = t - this.last[side] >= AUDIO_FIGHT_QUIET_SEC;
    this.last[side] = t;
    return first;
  }
}

/** The fields of a wire event that `mapCue` reads. The fields past the
 *  first four are optional, so a test states only what its rule reads. */
export interface CueEvent {
  kind: EventKind;
  victim: number;
  actor: number;
  subject: number;
  heal?: boolean;
  seaObject?: number;
  faction?: number;
  victimFaction?: number;
  ambush?: boolean;
  roundKillSource?: RoundKillSource;
}

/**
 * The cue one event plays, with the victim's hull class in hand.
 *
 * An EXPLOSION forks three ways. A sinking Beacon has its own voice: the
 * anchored hull is a structure and not a ship, so it must not sound like
 * one, and the line "Beacon destroyed." is true for the side that lost it
 * and for the side that sank it (R618). A sinking ship speaks for the
 * empire that lost the hull alone: the server names both sides on one
 * shared event, so a hull this commander sank plays the effect with no
 * words and the event `fleet_killed` speaks the kill (R730).
 *
 * A TORPEDO_HIT with `heal` is a repair and plays `SFX_REPAIR` (R2804),
 * with the line for the commander who fired it alone (R2811).
 *
 * Every other event reads the table above and nothing else.
 *
 * A gun HIT reads the fight clock: the first hit of a side speaks, every
 * later one is the effect alone (R629). The commander who fired is the
 * `actor`, so a hit they dealt is the dealt side and a hit on their hull
 * is the taken side. A splash never reaches here (scene.ts drops it). An
 * ambush hit this commander dealt plays `sfx-ambush-shot` in place of the
 * fight cue, and it speaks only when it opens the fight (R3118).
 *
 * `faction` is the viewer's faction, 0 for a viewer with none. A guard's
 * ROUND_KILL, a SIGNATURE, a FLIP and a CAPTURE fork on it (R3118-R3120).
 * A burst's ROUND_KILL answers nothing here: `burstCues` folds the kills
 * of one burst into one cue. `flips` is the gap of the flip cue.
 *
 * `classOf` answers for a hull the fleet layer still holds. The events of a
 * tick arrive BEFORE the state frame of the same tick (hub/loop.go), so the
 * hull the explosion names is still in the layer when this is asked.
 */
export function mapCue(
  e: CueEvent,
  classOf: (fleetId: number) => ShipClass | undefined,
  me: number,
  fight: FightClock,
  faction = 0,
  flips: CueGap | null = null,
): EventSfx | undefined {
  switch (e.kind) {
    case EventKind.EVENT_KIND_ROUND_KILL:
      return e.roundKillSource === RoundKillSource.ROUND_KILL_SOURCE_GUARD
        ? guardKillCue(e, me, faction) : undefined;
    case EventKind.EVENT_KIND_SIGNATURE:
      return signatureCue(e, me, faction);
    case EventKind.EVENT_KIND_FLIP:
      // The own faction's gains alone, one cue in the gap (R3120).
      if (faction === 0 || e.faction !== faction) return undefined;
      return !flips || flips.pass() ? SFX_SOV1 : undefined;
    case EventKind.EVENT_KIND_CAPTURE:
      if (faction !== 0 && e.faction === faction) return SFX_SOV2;
      if (faction !== 0 && e.victimFaction === faction) return SFX_OBJECT_LOST;
      return SFX_SOV2_QUIET;
  }
  if (COMBAT_KINDS.has(e.kind) && !involvesMe(e, me)) return undefined;
  if (e.kind === EventKind.EVENT_KIND_EXPLOSION) {
    if (classOf(e.victim) === ShipClass.SHIP_CLASS_BEACON) return SFX_BEACON_DESTROYED;
    // "Fleet lost." is the victim's line. A hull this commander sank
    // plays the effect alone, and `fleet_killed` speaks the kill (R595).
    return e.subject === me ? sfxForEventKind[e.kind] : SFX_SINK_QUIET;
  }
  if (e.kind === EventKind.EVENT_KIND_TORPEDO_HIT && e.heal) {
    return e.actor === me ? SFX_REPAIR : SFX_REPAIR_QUIET;
  }
  // A shot at a sea object opens no fight (v2-the-deep.md §11.2, R3031):
  // the shooter hears the effect alone, and the fight clock does not move.
  if (e.kind === EventKind.EVENT_KIND_HIT && (e.seaObject ?? 0) > 0) return SFX_FLEET_HIT;
  if (e.kind === EventKind.EVENT_KIND_HIT) {
    const side: FightSide = e.actor === me ? 'dealt' : 'taken';
    const opens = fight.opens(side);
    if (side === 'dealt' && e.ambush) return opens ? SFX_AMBUSH_SHOT : SFX_AMBUSH_SHOT_QUIET;
    if (!opens) return SFX_FLEET_HIT;
    return side === 'dealt' ? sfxForEventKind[e.kind] : SFX_TAKING_DAMAGE;
  }
  return sfxForEventKind[e.kind];
}

/**
 * The cue of a guard's kill (R3118), at the round. The side of the guard
 * hears the line: the commander of the Sentinel, or the team that holds a
 * Reef Breakwater, which names no commander (`actor` 0). The faction that
 * lost the round hears the effect alone. `subject` is 0 on a round kill,
 * so `victim_faction` is the test of the losing side. Every other viewer
 * hears nothing (R551).
 */
function guardKillCue(e: CueEvent, me: number, faction: number): EventSfx | undefined {
  const guardSide = e.actor !== 0
    ? me !== 0 && e.actor === me
    : faction !== 0 && e.faction === faction;
  if (guardSide) return SFX_GUARD_KILL;
  if (faction !== 0 && e.victimFaction === faction) return SFX_GUARD_KILL_QUIET;
  return undefined;
}

/**
 * The cue of a signature order (R3119). The commander who spent it hears
 * the effect alone, and a viewer of another faction hears the line: a
 * hostile spend is a warning, not combat, so the rule of R551 does not
 * hold for it. The server sends the event only to a viewer who holds the
 * hull. The spend of another commander of the own faction is silent.
 */
function signatureCue(e: CueEvent, me: number, faction: number): EventSfx | undefined {
  if (me !== 0 && e.actor === me) return SFX_SIGNATURE_ORDER_QUIET;
  if (faction !== 0 && e.faction !== faction) return SFX_SIGNATURE_ORDER;
  return undefined;
}

/** One cue with a place, in wire millipixels. The hub page imports this
 *  module too, so it converts no unit and pulls in no world geometry. */
export interface PlacedSfx { sfx: EventSfx; xMpx: number; yMpx: number }

/**
 * The round bursts of one event batch, one cue for each burst (R3118).
 *
 * A burst raises one ROUND_KILL for each round it kills, and every one of
 * them names the burst point as its source. The kills with one source
 * point are one burst, and the burst sounds once, at that point. The
 * commander who fired the bursting round hears the line, and a viewer of a
 * faction that lost a round hears the effect alone. Every other viewer
 * hears nothing (R551). The list keeps the order of the first kill.
 */
export function burstCues(
  events: readonly (CueEvent & { sourceXMpx: number; sourceYMpx: number })[],
  me: number, faction: number,
): PlacedSfx[] {
  const out: PlacedSfx[] = [];
  const at = new Map<string, number>();
  for (const e of events) {
    if (e.kind !== EventKind.EVENT_KIND_ROUND_KILL
        || e.roundKillSource !== RoundKillSource.ROUND_KILL_SOURCE_BURST) continue;
    const line = me !== 0 && e.actor === me;
    const lost = faction !== 0 && e.victimFaction === faction;
    if (!line && !lost) continue;
    const key = `${e.sourceXMpx}|${e.sourceYMpx}`;
    const i = at.get(key);
    if (i === undefined) {
      at.set(key, out.length);
      out.push({
        sfx: line ? SFX_TORPOR_BURST : SFX_TORPOR_BURST_QUIET,
        xMpx: e.sourceXMpx, yMpx: e.sourceYMpx,
      });
    } else if (line) out[i].sfx = SFX_TORPOR_BURST;
  }
  return out;
}

/**
 * The combat kinds: a launch, a hit, a blast, a sinking hull. The owner
 * ruled that these sound only for the two people in the exchange (R551):
 * a fight between two other empires is a picture on the map and not a
 * sound in this commander's room. A flip, a capture, a round kill and a
 * signature order are not in this set: `mapCue` forks each of them on the
 * viewer's faction (R3118-R3120).
 */
export const COMBAT_KINDS: ReadonlySet<EventKind> = new Set([
  EventKind.EVENT_KIND_LAUNCH,
  EventKind.EVENT_KIND_TORPEDO_HIT,
  EventKind.EVENT_KIND_TORPEDO_EXPIRE,
  EventKind.EVENT_KIND_HIT,
  EventKind.EVENT_KIND_EXPLOSION,
]);

/** True when this commander fired the shot or one of their hulls took it.
 *  `actor` is the commander who did it and `subject` the one it was done
 *  to (the server names both, R538); a quake blast names nobody. A viewer
 *  with no id (0) is involved in nothing. */
export function involvesMe(e: { actor: number; subject: number }, me: number): boolean {
  return me !== 0 && (e.actor === me || e.subject === me);
}

/** A ping sweep leaves an own base (render/sonar.ts) — positional, at the
 *  base, so a sweep across the viewport arrives from where it started. The
 *  server never hears about it: the sonar layer is presentation. */
export const SFX_PING: KnownSfxId = 'sfx-sonar-sweep';

/** A clan asks this account to answer it (R550). The hub's CLAN panel plays
 *  it on the first sight of a new application, and the hub is outside the
 *  water, so the clip is a console sound (audio.md §4.4). It is a constant
 *  and not a literal because it is the one registered id the menu shell
 *  plays: `KnownSfxId` fails the build on a typo. */
export const SFX_CLAN_HAIL: KnownSfxId = 'sfx-clan-hail';

/**
 * The cue the map's arrival sounds, or null where the arrival stays silent
 * (R598, audio.md §4.4).
 *
 * The season map plays the arrival on a login and on a season roll, and
 * `sfx-map-arrival` starts with the veil. A resync is a
 * reconnect inside the same season: that commander is looking at this map
 * already, so the arrival there is a short fade and it says nothing. The
 * rule is a function and not a literal at the call site, because it is the
 * one part of the trigger a test can hold.
 *
 * A match plays no arrival at all (`fast/arcade/match.ts`), so the arcade
 * page never reaches this cue.
 */
export function arrivalCue(kind: SpawnKind): KnownSfxId | null {
  return kind === 'resync' ? null : 'sfx-map-arrival';
}
